import RequestToken from '../../../../tokens/RequestToken';
import {CommandRequester} from '../../../../CommandRequester';
import RequestAPI from '../../../RequestAPI';
import {CommandTypes, ConfigEvents, AsyncCommandEvent} from '@jibo/command-protocol';

/**
 * @class SetConfigToken
 * @extends RequestToken
 * @hideconstructor
 */
export class SetConfigToken extends RequestToken<JIBO.v1.SetConfigRequest> {

    /** @private */
    constructor(owner:CommandRequester, protocol: JIBO.v1.SetConfigRequest) {
        super(owner, protocol);
    }

    /**
     * @private
     */
    public handleAck(ack:JIBO.v1.Acknowledgement) {
        //handle ack
        if (ack.Response.ResponseCode >= 400) {
            this.isComplete = true;
            this._complete.reject(ack.Response);
            return;
        }
    }

    /**
     * @private
     */
    public handleEvent(event:JIBO.v1.EventMessage) {
        //handle event
        switch (event.EventBody.Event) {
            case AsyncCommandEvent.Stop:
                this.isComplete = true;
                this._complete.resolve();
                break;
            case AsyncCommandEvent.Error:
                this.isComplete = true;
                this._complete.reject(event.EventBody);
                break;
        }
    }
}

/**
 * @class GetConfigToken
 * @extends RequestToken
 * @hideconstructor
 */
export class GetConfigToken extends RequestToken<JIBO.v1.GetConfigRequest, JIBO.v1.ConfigEvent> {

    /** @private */
    constructor(owner:CommandRequester, protocol: JIBO.v1.GetConfigRequest) {
        super(owner, protocol);
    }

    /**
     * @private
     */
    public handleAck(ack:JIBO.v1.Acknowledgement) {
        //handle ack
        if (ack.Response.ResponseCode >= 400) {
            this.isComplete = true;
            this._complete.reject(ack.Response);
            return;
        }
    }

    /**
     * @private
     */
    public handleEvent(event:JIBO.v1.EventMessage) {
        //handle event
        let message = event.EventBody;
        switch (message.Event) {
            case ConfigEvents.onConfig:
                this.isComplete = true;
                this._complete.resolve(message);
                break;
        }
    }
}

/**
 * Internal only Config class
 * @class CommandRequester.config.Config
 * @intdocs
 */
export class Config extends RequestAPI<JIBO.v1.GetConfigRequest | JIBO.v1.SetConfigRequest, GetConfigToken | SetConfigToken> {

    /**
     * Generate Get Config Protocol
     * @method CommandRequester.config.Config#generateProtocol
     * @returns {SetConfigRequest | GetConfigRequest}
     * @intdocs
     */
    static generateProtocol(options?: JIBO.v1.SetConfigOptions): JIBO.v1.GetConfigRequest | JIBO.v1.SetConfigRequest {
        if(options) {
            return {
                Type: CommandTypes.SetConfig,
                Options: options
            };
        }
        return {
            Type: CommandTypes.GetConfig
        };
    }

    /**
     * Create ConfigToken from ConfigRequest protocol.
     * @method CommandRequester.config.Config#generateToken
     * @param {GetConfigRequest | SetConfigRequest} protocol - ConfigRequest protocol to generate a ConfigToken from.
     * @param {boolean} [andSend=false] - `true` if the generated ConfigToken should also be sent as a request.
     * @return {GetConfigToken | SetConfigRequest}
     * @intdocs
     */
    public generateToken(protocol: JIBO.v1.GetConfigRequest | JIBO.v1.SetConfigRequest, andSend=false): GetConfigToken | SetConfigToken {
        let token: GetConfigToken | SetConfigToken;
        if(protocol.Type === CommandTypes.GetConfig) {
            token = new GetConfigToken(this.owner, protocol);
        } else if(protocol.Type === CommandTypes.SetConfig) {
            token = new SetConfigToken(this.owner, protocol);
        }

        if (andSend) {
            super.sendToken(token);
        }
        return token;
    }

    /**
     * Send ConfigToken request.
     * <p>NOTE: Implementation in base class {@link RequestAPI}</p>
     * @method CommandRequester.config.Config#sendToken
     * @param {GetConfigToken | SetConfigToken} token - ConfigToken to send.
     * @intdocs
     */
    public sendToken(token: GetConfigToken | SetConfigToken): void {
        this.owner.sendToken(token);
    }
}